import { SETTING, SETTING_TYPE } from "./constants";
import {
  getLocalStorageItem,
  getSessionStorageItem,
  localStorageSafe,
  sessionStorageSafe,
  setLocalStorageItem,
  setSessionStorageItem
} from "../storage/storage";
import { consoleLogWarning } from "../console";
import { validateSettingType } from "./validators";

// use only to product an individual method for an app e.g. {validateSettingsMainApp}
export const _validateSettings = (
  settings, settingsValidators, settingsDefault, holderFixedSettings
) => {
  for (const id in settings) {
    _validateSetting(id, settings[id], settingsValidators, settingsDefault, holderFixedSettings);
  }
};

// use only to product an individual method for an app e.g. {validateSettingMainApp}
export const _validateSetting = (
  id, value, settingsValidators, settingsDefault, holderFixedSettings
) => {
  const settingObj = SETTING[id];

  if (settingObj) {
    if (
      !validateSettingType(settingObj.type, value)
      || (settingsValidators[id] && !settingsValidators[id](value))
    ) {
      const valueDefault = (settingsDefault && settingsDefault[settingObj.id])
        ?? settingObj.default;

      // Consider a setting valid, if its value equals the default value.
      const isValidFinal = value === valueDefault;

      if (!isValidFinal) {
        console.log("%cSetting is not valid", "background: yellow; color: black;", {
          settingObj,
          settingValue: value,
          settingsValidators,
          settingsDefault,
          holderFixedSettings,
        });

        if (holderFixedSettings) {
          holderFixedSettings[id] = valueDefault;
        }
      }

      return isValidFinal;
    }

    return true;
  }

  return undefined;
};


// use only to product an individual method for an app e.g. {getStoredSettingMainApp}
export const _getStoredSetting = (id, valueDefault) => {
  const setting = SETTING[id];
  if (!setting) {
    return undefined;
  }

  const settingDefault = valueDefault ?? setting.default;

  let outValue;

  const storage = setting.session ? sessionStorageSafe : localStorageSafe;
  if (setting.type === SETTING_TYPE.number) {
    const intValue = storage.getItem(setting.id);
    if (intValue !== undefined && intValue !== null) {
      outValue = parseInt(intValue, 10);
    } else if (settingDefault) {
      outValue = settingDefault;
    }
  } else if (setting.type === SETTING_TYPE.bool) {
    const value = storage.getItem(setting.id);
    outValue = settingDefault;
    if (value !== undefined && value !== null) {
      outValue = value === "1";
    }
  } else if (
    setting.type === SETTING_TYPE.string
    || setting.type === SETTING_TYPE.array
    || setting.type === SETTING_TYPE.object
  ) {
    outValue = setting.session
      ? getSessionStorageItem(
        setting.id,
        settingDefault,
        setting.type === SETTING_TYPE.array || setting.type === SETTING_TYPE.object
      )
      : getLocalStorageItem(
        setting.id,
        settingDefault,
        setting.type === SETTING_TYPE.array || setting.type === SETTING_TYPE.object
      );
  }
  return outValue;
};

// use only to product an individual method for an app e.g. {getStoredSettingsMainApp}
export const _getStoredSettings = (location, settingsDefault) => {
  let settingsObj = {};
  Object.keys(SETTING).forEach((id) => {
    if (SETTING[id].locations.includes(location)) {
      settingsObj[id] = _getStoredSetting(id, settingsDefault && settingsDefault[id]);
    }
  });
  return settingsObj;
};


// use only to product an individual method for an app e.g. {getDefaultSettingsMainApp}
export const _getDefaultSettings = (location, settingsDefault) => {
  return Object.entries(SETTING).reduce((acc, [id, config]) => {
    if (config.locations.includes(location)) {
      acc[id] = (settingsDefault && settingsDefault[id]) ?? config.default;
    }

    return acc;
  }, {});
};

export const storeSetting = (id, value) => {
  const setting = SETTING[id];

  if (setting) {
    let valueToStore = value;
    if (setting.type === SETTING_TYPE.bool) {
      valueToStore = value ? "1" : "0";
    }
    if (setting.session) {
      setSessionStorageItem(
        setting.id,
        valueToStore,
        setting.type === SETTING_TYPE.array || setting.type === SETTING_TYPE.object
      );
    } else {
      setLocalStorageItem(
        setting.id,
        valueToStore,
        setting.type === SETTING_TYPE.array || setting.type === SETTING_TYPE.object
      );
    }
    return setting;
  }

  consoleLogWarning("Settings", "Setting not defined: ", id, setting);

  return undefined;
};
